import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Alert, Space, Typography, Popconfirm, Grid } from 'antd';
import { BuildOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ResponsiveTable from '../../../components/Common/ResponsiveTable';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const CompaniesTab = ({
  companies,
  loading,
  onEdit,
  onDelete,
  onNew,
  readOnly = false
}) => {
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = useRef(null);
  const screens = useBreakpoint();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const companyColumns = [
    {
      title: 'Razón Social',
      dataIndex: 'tax_name',
      key: 'tax_name',
    },
    {
      title: 'RFC',
      dataIndex: 'tax_id',
      key: 'tax_id',
    },
    {
      title: 'Régimen Fiscal',
      dataIndex: 'fiscal_regimen',
      key: 'fiscal_regimen',
      responsive: ['md'],
    },
    {
      title: 'Código Postal',
      dataIndex: 'tax_location',
      key: 'tax_location',
      responsive: ['lg'],
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          >
            {screens.md ? 'Editar' : ''}
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta empresa?"
            description="Esta acción no se puede deshacer y eliminará todos los datos asociados."
            onConfirm={() => onDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              {screens.md ? 'Eliminar' : ''}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(col => !(readOnly && col.key === 'actions'));

  return (
    <Card>
      <div style={{ 
        marginBottom: '16px', 
        display: 'flex', 
        flexDirection: screens.xs ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: screens.xs ? 'flex-start' : 'center',
        gap: screens.xs ? '16px' : '0'
      }}>
        <Title level={4} style={{ margin: 0 }}>
          Gestión de Empresas
        </Title>
        {!readOnly && <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onNew}
          block={screens.xs}
        >
          Nueva Empresa
        </Button>}
      </div>

      <Alert
        message="Información"
        description="Aquí puedes gestionar las empresas asociadas a tu cuenta. Cada empresa puede tener su propia configuración fiscal y certificados."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <div ref={topPaginationRef} />
      <ResponsiveTable
        columns={companyColumns}
        dataSource={companies}
        rowKey="id"
        loading={loading}
        locale={{ emptyText: 'No hay empresas registradas' }}
        pagination={{
          position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
        }}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};

export default CompaniesTab;